import type { UserPrivate } from '@repo/types/users';

import { useResendInvite } from '@repo/api/user/useResendInvite';
import { ErrorCodes } from '@repo/lib/errorCodes';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { isAxiosError } from 'axios';
import { format } from 'date-fns';
import { MailIcon } from 'lucide-react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

import { getInviteStatus } from './inviteStatus';

/**
 * Props for the ResendInvite component.
 * @property {UserPrivate | null} [user] - The user who should receive a new signup link.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(value: boolean) => void} onOpenChange - Callback to change dialog open state.
 */
interface ResendInviteProps {
  user?: UserPrivate | null;
  open: boolean;
  onOpenChange: (value: boolean) => void;
}

export default function ResendInvite({ user, open, onOpenChange }: ResendInviteProps) {
  const { mutateAsync, isPending } = useResendInvite();

  const invite = user ? getInviteStatus(user) : null;
  const previousInviteCopy =
    invite?.state === 'valid'
      ? `Obecne zaproszenie wysłano ${format(invite.sentAt, 'dd.MM.yyyy HH:mm')}, jest ważne do ${format(invite.expiresAt, 'dd.MM.yyyy HH:mm')} i przestanie działać po wysłaniu nowego.`
      : invite?.state === 'expired'
        ? `Poprzednie zaproszenie wygasło ${format(invite.expiresAt, 'dd.MM.yyyy HH:mm')}.`
        : 'Ten użytkownik nie ma jeszcze zaproszenia.';

  const onClose = () => {
    onOpenChange(false);
  };

  const onSubmit = () => {
    if (!user) return;

    mutateAsync(user.id)
      .then(() => {
        toast.success('Zaproszenie wysłane', { description: `Nowy link rejestracyjny wysłano na adres ${user.email}.` });
      })
      .catch((error) => {
        // The default copy for these codes is addressed to the account owner, not the admin.
        const code = isAxiosError<{ code?: string }>(error) ? error.response?.data.code : undefined;
        switch (code) {
          case ErrorCodes.AUTH.ALREADY_SIGNED_UP:
            toast.error('Użytkownik już zarejestrowany', { description: 'Ten użytkownik dokończył już rejestrację, więc zaproszenie nie jest potrzebne.' });
            return;
          case ErrorCodes.AUTH.ACCOUNT_DEACTIVATED:
            toast.error('Konto dezaktywowane', { description: 'Aktywuj konto użytkownika, zanim wyślesz mu zaproszenie.' });
            return;
          case ErrorCodes.FORBIDDEN.USER.LVL_UPGRADE:
            toast.error('Brak uprawnień', { description: 'Tylko administrator może wysłać zaproszenie nauczycielowi lub administratorowi.' });
            return;
          default:
            toasterMessageHandler(baseErrorMessages, error);
        }
      });
    onClose();
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={'sm:max-w-96'}>
        <AlertDialogBody>
          <AlertDialogHeader>
            <FeaturedIcon size={'lg'}>
              <MailIcon />
            </FeaturedIcon>
            <div className={'flex min-w-0 grow flex-col gap-0.5'}>
              <AlertDialogTitle>Wyślij ponownie zaproszenie</AlertDialogTitle>
              <AlertDialogDescription className={'text-muted-foreground text-xs'}>Nowy link rejestracyjny, ważny 30 dni.</AlertDialogDescription>
            </div>
          </AlertDialogHeader>
          <div className={'flex flex-col gap-3 px-4 pb-4 text-sm hyphens-none'}>
            <div className={'bg-accent flex flex-col gap-0.5 rounded-md px-3 py-2'}>
              <span className={'text-foreground font-medium'}>{user ? `${user.name} ${user.surname}` : ''}</span>
              <span className={'text-muted-foreground break-all'}>{user?.email}</span>
            </div>
            <p className={'text-text-secondary'}>{previousInviteCopy}</p>
          </div>
        </AlertDialogBody>
        <AlertDialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
          <AlertDialogCancel variant={'tertiary'} onClick={onClose}>
            Zamknij
          </AlertDialogCancel>
          <AlertDialogAction onClick={onSubmit} disabled={isPending}>
            Wyślij
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
